import React from 'react';
import { FilterOptions } from '../../../utils/productListFilters';
import { ProductListSortOption } from '../../../utils/productListSort';
import { useIsElementVisible } from '../../../shared/hooks/useIsElementVisible';
import { CusProductListUseProductListFiltersParams } from '../cus-product-list.data';

function normalize(filters: FilterOptions): FilterOptions {
    const result: FilterOptions = {};
    if (filters.gender) {
        result.gender = filters.gender;
    }
    if (filters.priceRange) {
        result.priceRange = filters.priceRange;
    }
    if (filters.categoryIds?.length) {
        result.categoryIds = filters.categoryIds;
    }
    if (filters.sizes?.length) {
        result.sizes = filters.sizes;
    }
    if (filters.colors?.length) {
        result.colors = filters.colors;
    }
    return result;
}

export default function useProductListFilters({
    onSearch,
    onApply,
    currentSort,
    filters,
    initialFilters
}: CusProductListUseProductListFiltersParams) {
    const [isOpen, setIsOpen] = React.useState(false);
    const { ref: toolbarSentinelRef, isVisible: isToolbarVisible } = useIsElementVisible<HTMLDivElement>();

    const [activeFilters, setActiveFilters] = React.useState<FilterOptions>(initialFilters);
    const [draftFilters, setDraftFilters] = React.useState<FilterOptions>(initialFilters);
    const [draftSort, setDraftSort] = React.useState<ProductListSortOption>(currentSort);

    const closeDrawer = () => setIsOpen(false);

    const close = () => {
        // Discard pending draft changes when closing without applying.
        setDraftFilters(activeFilters);
        setDraftSort(currentSort);
        closeDrawer();
    };

    const toggle = () => {
        if (isOpen) {
            close();
            return;
        }
        setDraftSort(currentSort);
        setIsOpen(true);
    };

    const activeCount =
        (activeFilters.gender ? 1 : 0) +
        (activeFilters.priceRange ? 1 : 0) +
        (activeFilters.categoryIds?.length ?? 0) +
        (activeFilters.sizes?.length ?? 0) +
        (activeFilters.colors?.length ?? 0);

    const categoryNames = React.useMemo(() => {
        const map: Record<string, string> = {};
        for (const cat of filters.categories) {
            map[cat.id] = cat.displayName;
        }
        return map;
    }, [filters.categories]);

    const handleSearch = () => {
        const filterOptions = normalize(draftFilters);
        setActiveFilters(filterOptions);
        setDraftFilters(filterOptions);
        onApply(filterOptions, draftSort);
        closeDrawer();
    };

    const handleRemoveBullet = (updated: FilterOptions) => {
        setActiveFilters(updated);
        setDraftFilters(updated);
        onSearch(updated);
    };

    const handleClearAll = () => {
        setActiveFilters({});
        setDraftFilters({});
        onSearch({});
    };

    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    const showFloatingBar = !isToolbarVisible && !isOpen;

    return {
        showFloatingBar,
        handleClearAll,
        handleRemoveBullet,
        toggle,
        close,
        activeCount,
        categoryNames,
        activeFilters,
        draftFilters,
        setDraftFilters,
        draftSort,
        setDraftSort,
        toolbarSentinelRef,
        handleSearch,
        isOpen
    };
}
